import React from "react";
import { useLocation } from "react-router-dom";
import { Logo, ScreenSizeRender } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import classNames from "classnames";
import { Colors } from "@jsq/ds/styles";
import { useAppContext, type ManagerArena } from "../AppContext";
import styles from "./styles.scss";
import jsqLogo from "./jsq_logo.svg";

function getCurrentManager(currentRoute: string[]) {
  let currentManager = "";
  /* currentRoute looks like ["", "i", "rockstonecap"] */

  if (currentRoute.length >= 2) {
    if (currentRoute[1] === "lp") {
      /* For multi-manager context */
      // eslint-disable-next-line prefer-destructuring
      currentManager = currentRoute[1];
    }
    if (currentRoute[1] === "i" && currentRoute.length >= 3) {
      /* Single-manager context */
      // eslint-disable-next-line prefer-destructuring
      currentManager = currentRoute[2];
    }
  }
  return currentManager;
}

type Props = {
  arena: ManagerArena | null;
  currentManager: string;
};

const GpArenaWrapper: React.FunctionComponent<Props> = ({ arena, currentManager }) => {
  const secondLevelDomain = arena ? arena.secondLevelDomain : "lp";
  const arenaName = arena ? arena.name : "All investments and settings";
  const redirectUrl = arena ? `/i/${arena.secondLevelDomain}` : "/lp";
  const isSelected = currentManager === secondLevelDomain;

  if (isSelected) {
    return (
      <div
        className={classNames(
          styles.ContextSwitcher__managerWrapper,
          styles.ContextSwitcher__managerWrapperCurrentSelected,
        )}
        data-cy="context-arena-selector"
      >
        {arena ? (
          <Logo
            backgroundColor={arena.logoBackgroundColor}
            className={styles.ContextSwitcher__logo}
            url={arena.logoUrl}
            name={arena.name}
            size="large"
          />
        ) : (
          <img
            alt="Juniper Square Logo"
            src={jsqLogo}
            className={styles.ContextSwitcher__logo}
            data-cy="jsq-logo"
          />
        )}
        <div className={`text-md strong ${styles.ContextSwitcher__arenaName}`}>{arenaName}</div>
        <Icon type="check" color={Colors.Primary600} data-cy="arena-check" />
      </div>
    );
  }

  return (
    <a
      href={redirectUrl}
      target="_blank"
      rel="noreferrer"
      className={styles.ContextSwitcher__managerWrapper}
      data-cy="context-arena-selector"
    >
      {arena ? (
        <Logo
          backgroundColor={arena.logoBackgroundColor}
          className={styles.ContextSwitcher__logo}
          url={arena.logoUrl}
          name={arena.name}
          size="large"
        />
      ) : (
        <img alt="Juniper Square Logo" src={jsqLogo} className={styles.ContextSwitcher__logo} />
      )}
      <div className={`text-md strong ${styles.ContextSwitcher__arenaName}`}>{arenaName}</div>
      <div className={styles.ContextSwitcher__actionIcon}>
        <Icon type="external-link" />
      </div>
    </a>
  );
};

const ContextSwitcher: React.FunctionComponent = () => {
  const { managerArenas } = useAppContext();
  const location = useLocation();
  const currentRoute = location.pathname ? location.pathname.split("/") : [];
  const currentManager = getCurrentManager(currentRoute);

  const contextSwitcherBody = (
    <span data-cy="context-switcher-body" data-testid="context-switcher-body">
      <GpArenaWrapper arena={null} currentManager={currentManager} />

      <div
        className={`text-md uppercase secondary ${styles.ContextSwitcher__managerLabel}`}
        data-cy="context-switcher-manager-label"
      >
        INVESTOR PORTALS
      </div>

      {managerArenas.map((arena) => (
        <GpArenaWrapper arena={arena} currentManager={currentManager} key={arena.id} />
      ))}
    </span>
  );

  return (
    <>
      <ScreenSizeRender small medium>
        {contextSwitcherBody}
      </ScreenSizeRender>
      <ScreenSizeRender large>
        <div className={styles.ContextSwitcher__wrapper}>{contextSwitcherBody}</div>
      </ScreenSizeRender>
    </>
  );
};

export default ContextSwitcher;
